import { Component, OnInit, inject } from '@angular/core';
import { StringHelper } from '@mpt-fo/common/core/helpers/strings.helper';
import { OnboardingV2Service } from '@mpt-fo/common/core/services/onboarding-v2.service';
import { CustomTimelineVersion } from '@mpt-fo/common/kamona/model/kamonal-model';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import { BvnNinMisMatchError } from '../../../model/onboarding-v2-model';
import { ScreenManagerService } from '@mpt-fo/common/core/services/screen-manager.service';

@Component({
	selector: 'app-kyc-bvn-nin-mismatch-modal',
	templateUrl: './kyc-bvn-nin-mismatch-modal.component.html',
	styleUrls: ['./kyc-bvn-nin-mismatch-modal.component.scss']
})
export class KycBvnNinMismatchModalComponent implements OnInit {
	transformedData: Array<{
		label: string;
		ninFields: Array<{ label: string; value: string; compareValue: string; }>;
		bvnFields: Array<{ label: string; value: string; compareValue: string; }>;
		issues: Array<{ issueType: string; message: string; }>;
	}> = [];
	isMobile = false;
	breakPoint = 768;
	bvn: string;
	nin: string;
	fullName: string;
	initials: string;
	modalData: { nin?: string, bvn?: string, apiData: BvnNinMisMatchError };
    customTimelineVersion = CustomTimelineVersion;
	description: string;
	readonly nzModalData = inject(NZ_MODAL_DATA, { optional: true }) ?? {};

	constructor(
		public modalRef: NzModalRef,
		private onboardingService: OnboardingV2Service,
		private screenManagerService: ScreenManagerService,
	) {
	}

	ngOnInit(): void {
		this.modalRef.componentInstance = { ...this.modalRef.componentInstance, ...this.nzModalData };
		this.modalData = this.modalRef.componentInstance.data;
		this.isMobile = this.screenManagerService.isMobile();
		this.screenManagerService.isMobile$.subscribe((isMobile) => {
			this.isMobile = isMobile;
		});
		this.initKycData();
		this.transformData(this.modalData.apiData);
	}

	initKycData() {
		this.description = `We have not been able to validate your ${this.modalData.bvn ? 'BVN' : 'NIN'} due to the following issues`;
		this.fullName = this.onboardingService.getFullName();
		this.initials = StringHelper.getInitials(this.fullName);
		if (this.modalData.bvn) {
			this.nin = this.onboardingService.getNationalIdentificationNumberFromKyc();
			this.bvn = this.modalData.bvn;
		} else {
			this.bvn = this.onboardingService.getBankVerificationNumberFromKyc();
			this.nin = this.modalData.nin;
		}
	}

	transformData(data: BvnNinMisMatchError) {
		const { nameIssue, phoneNumberIssues, dateOfBirthIssues } = data;
		if (nameIssue) {
			this.transformedData.push({
				label: 'Name Issues',
				ninFields: [
					{
						label: 'NIN First Name',
						value: nameIssue.ninName.firstName,
						compareValue: nameIssue.bvnName.firstName,
					},
					{
						label: 'NIN Middle Name',
						value: nameIssue.ninName.middleName,
						compareValue: nameIssue.bvnName.middleName,
					},
					{
						label: 'NIN Last Name',
						value: nameIssue.ninName.lastName,
						compareValue: nameIssue.bvnName.lastName,
					},
				],
				bvnFields: [
					{
						label: 'BVN First Name',
						value: nameIssue.bvnName.firstName,
						compareValue: nameIssue.ninName.firstName,
					},
					{
						label: 'BVN Middle Name',
						value: nameIssue.bvnName.middleName,
						compareValue: nameIssue.ninName.middleName,
					},
					{
						label: 'BVN Last Name',
						value: nameIssue.bvnName.lastName,
						compareValue: nameIssue.ninName.lastName,
					},
				],
				issues: this.getNameIssues(nameIssue),
			});
		}
		if (phoneNumberIssues) {
			this.transformedData.push({
				label: 'Phone Number Issues',
				ninFields: [
					{
						label: 'NIN Phone No.',
						value: phoneNumberIssues.ninPhoneNumber,
						compareValue: phoneNumberIssues.bvnPhoneNumber,
					},
				],
				bvnFields: [
					{
						label: 'BVN Phone No.',
						value: phoneNumberIssues.bvnPhoneNumber,
						compareValue: phoneNumberIssues.ninPhoneNumber,
					},
				],
				issues: phoneNumberIssues.issues,
			});
		}
		if (dateOfBirthIssues) {
			this.transformedData.push({
				label: 'Date of Birth Issues',
				ninFields: [
					{
						label: 'NIN D.O.B',
						value: dateOfBirthIssues.ninDateOfBirth,
						compareValue: dateOfBirthIssues.bvnDateOfBirth,
					},
				],
				bvnFields: [
					{
						label: 'BVN D.O.B',
						value: dateOfBirthIssues.bvnDateOfBirth,
						compareValue: dateOfBirthIssues.ninDateOfBirth,
					},
				],
				issues: dateOfBirthIssues.issues,
			});
		}
	}

	getNameIssues(nameIssue: BvnNinMisMatchError['nameIssue']) {
		if (nameIssue.issues?.length) {
			return nameIssue.issues;
		}
		const issueType = 'Missing Name';
		const getMessage = (missingField) => `There is no ${missingField} attached to your ID. To fix this issue visit your nearest enrolment bank branch to update your details.`;
		const issues = [];
		if (!nameIssue.bvnName?.firstName || !nameIssue.ninName?.firstName) {
			issues.push({ issueType, message: getMessage('first name') });
		}
		if (!nameIssue.bvnName?.middleName || !nameIssue.ninName?.middleName) {
			issues.push({ issueType, message: getMessage('middle name') });
		}
		if (!nameIssue.bvnName?.lastName || !nameIssue.ninName?.lastName) {
			issues.push({ issueType, message: getMessage('last name') });
		}
		return issues;
	}

	closeModal() {
		this.modalRef.destroy();
	}

	getButtonText() {
		return this.isMobile ? 'Go Back' : 'Close';
	}
}
